/**
 * SendOffNeonFarewell — Interactive send-off invitation with 3D animations.
 *
 * Features: Neon aesthetic tailored for farewell, 3D animations, memories wall,
 * guest messages, countdown to departure, and Marathi/English bilingual support.
 *
 * id: `send-off-neon-farewell`
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { HeroFarewellSection } from './sections/HeroFarewellSection';
import { CountdownFarewellSection } from './sections/CountdownFarewellSection';
import { MemoriesWallSection } from './sections/MemoriesWallSection';
import { GuestMessagesSection } from './sections/GuestMessagesSection';
import { VenueDetailsSection } from './sections/VenueDetailsSection';
import { RSVPFarewellSection } from './sections/RSVPFarewellSection';
import { ParticleBackground } from '../BirthdayNeonBash/sections/ParticleBackground';
import './sendoff-farewell.css';

const META: TemplateMetadata = {
  id: 'send-off-neon-farewell',
  name: 'Send-off: Neon Farewell',
  occasionType: 'SEND_OFF',
  description: 'Modern interactive send-off invitation with emotional 3D animations, memory wall, guest messages, and countdown to departure.',
  isPremium: true,
  tags: ['send-off', 'farewell', 'neon', 'interactive', '3d', 'emotional', 'memories', 'premium'],
  colorScheme: {
    primary: '#00D9FF',
    accent: '#FF1493',
    bg: '#0A0E27',
  },
};

export function SendOffNeonFarewell({ data }: { data: InvitationData }) {
  const customDataAny = (data.customData || {}) as any;
  const sendoffData = customDataAny.sendoff || {};
  const [language, setLanguage] = useState<'en' | 'mr'>('en');
  const [messagesCount, setMessagesCount] = useState(0);

  const content = useMemo(() => {
    const personName = sendoffData.personName || 'Our Friend';
    const personNameMr = sendoffData.personNameMr || personName;
    const departure = data.eventDate ? new Date(data.eventDate) : new Date();
    const venueName = data.eventVenue || 'Gathering Place';
    const venueNameMr = sendoffData.eventVenueMr || venueName;

    return {
      person: {
        name: personName,
        nameMr: personNameMr,
        photo: sendoffData.photo || data.mediaUrls?.[0] || 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=500',
        destination: sendoffData.destination || 'New Adventures Await',
        destinationMr: sendoffData.destinationMr || 'नव्या साहस आपेक्षित आहे',
      },
      event: {
        date: departure,
        dateStr: departure.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
        dateMr: departure.toLocaleDateString('mr-IN', { weekday: 'long', month: 'short', day: 'numeric' }),
        time: data.eventTime || '6:00 PM',
        venue: venueName,
        venueMr: venueNameMr,
        message: sendoffData.message || 'Join us as we celebrate and bid farewell',
        messageMr: sendoffData.messageMr || 'आम्हाला विदा करण्यासाठी सामील व्हा',
      },
      photos: data.mediaUrls?.slice(0, 5) || [],
      invitationId: data.invitationId ?? data.id ?? null,
      isLive: data.status === 'PUBLISHED' || data.status === 'PROCESSING',
    };
  }, [data, sendoffData]);

  return (
    <div className="sendoff-neon-farewell min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-black text-white overflow-hidden">
      <ParticleBackground />

      {/* Language Toggle */}
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        <button
          onClick={() => setLanguage('en')}
          className={`px-4 py-2 rounded-full font-bold transition-all ${
            language === 'en'
              ? 'bg-cyan-500 text-black shadow-lg shadow-cyan-500'
              : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
          }`}
        >
          EN
        </button>
        <button
          onClick={() => setLanguage('mr')}
          className={`px-4 py-2 rounded-full font-bold transition-all ${
            language === 'mr'
              ? 'bg-pink-500 text-white shadow-lg shadow-pink-500'
              : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
          }`}
        >
          मराठी
        </button>
      </div>

      <main className="relative z-10">
        <HeroFarewellSection content={content} language={language} />
        <CountdownFarewellSection departureDate={content.event.date} language={language} />
        {content.photos.length > 0 && <MemoriesWallSection photos={content.photos} language={language} />}
        <GuestMessagesSection
          invitationId={content.invitationId}
          isLive={content.isLive}
          language={language}
          onMessageAdded={() => setMessagesCount(m => m + 1)}
          totalMessages={messagesCount}
        />
        <VenueDetailsSection venue={content.event} language={language} />
        <RSVPFarewellSection invitationId={content.invitationId} isLive={content.isLive} language={language} />
      </main>

      {/* Footer */}
      <div className="relative z-10 text-center py-12 text-gray-500 text-sm">
        <p>🌟 {language === 'mr' ? 'नेहमी आमच्या हृदयात राहा' : 'Forever in our hearts'}</p>
      </div>
    </div>
  );
}

registry.register({ metadata: META, component: SendOffNeonFarewell });
